import { APITypes } from '../primary-record-types';

export type HandleFastenRedirectReturnValue = {
    shouldBlockAppLoad: boolean;
};

const clientTypeKey = 'fastenRedirectSourceClientType';

const safelySetClientTypeInLocalStorage = (clientType: string) => {
    try {
        localStorage.setItem(clientTypeKey, clientType);
    } catch (e) {
        console.error('Unable to set client type in local storage', e);
    }
};

const safelyGetClientTypeFromLocalStorage = (): string => {
    try {
        return localStorage.getItem(clientTypeKey) || 'prod-web';
    } catch (e) {
        return 'prod-web';
    }
};

const sourceClientTypeUrls: Record<APITypes.sharedTypes.ClientType, string> = {
    'mobile-dev': 'primary-record-app://redirect',
    mobile: 'primary-record-app://redirect',
    'local-web': 'http://localhost:19006/redirect',
    'dev-web': 'https://dev.primaryrecord.app/redirect',
    'prod-web': 'https://www.primaryrecord.app/redirect',
};

/**
 * Trampoline for Fasten OAuth — same pattern as handle1UpHealthRedirect.ts.
 *
 * Both legs use the same URL: /handle-fasten-redirect
 *
 * LEG 1 — Outbound: /handle-fasten-redirect?connectUrl=...&sourceClientType=...
 *   Saves sourceClientType to localStorage. Redirects browser to Fasten OAuth URL.
 *
 * LEG 2 — Return: /handle-fasten-redirect?org_connection_id=...
 *   Fasten redirects back here after OAuth. Reads sourceClientType from localStorage.
 *   Redirects to the correct client origin's /redirect with Fasten params forwarded.
 */
export function handleFastenRedirect(): HandleFastenRedirectReturnValue {
    const location = window?.location?.href;

    if (!location) {
        return { shouldBlockAppLoad: false };
    }

    if (!location.includes('handle-fasten-redirect')) {
        return { shouldBlockAppLoad: false };
    }

    const url = new URL(location);
    const sourceClientType = url.searchParams.get('sourceClientType');
    const connectUrl = url.searchParams.get('connectUrl');

    if (sourceClientType && connectUrl) {
        // LEG 1: Outbound — save client type and redirect to Fasten OAuth
        safelySetClientTypeInLocalStorage(sourceClientType);

        // Store port for local dev bounce-back (supports 19006, 19007, etc.)
        const localPort = url.searchParams.get('localPort');
        if (localPort) {
            try {
                localStorage.setItem('fastenRedirectLocalPort', localPort);
            } catch {
                // ignore
            }
        }

        window.location.href = decodeURIComponent(connectUrl);
    } else {
        // LEG 2: Return — Fasten redirected back with org_connection_id or error.
        // Bounce to the original source client's /redirect page.
        const clientType = safelyGetClientTypeFromLocalStorage();
        // @ts-expect-error TS7053
        let destinationUrl: string = sourceClientTypeUrls[clientType] || sourceClientTypeUrls['prod-web'];

        // For local-web, use the stored port
        if (clientType === 'local-web') {
            try {
                const port = localStorage.getItem('fastenRedirectLocalPort') || '19006';
                destinationUrl = `http://localhost:${port}/redirect`;
            } catch {
                // fallback already set
            }
        }

        // Forward Fasten's query params (org_connection_id, error, error_description, etc.)
        const fastenParams = new URLSearchParams();
        for (const [key, value] of url.searchParams.entries()) {
            fastenParams.set(key, value);
        }
        const paramString = fastenParams.toString();
        if (paramString) {
            destinationUrl += (destinationUrl.includes('?') ? '&' : '?') + paramString;
        }

        window.location.href = destinationUrl;
    }

    return { shouldBlockAppLoad: true };
}
